
import React, { useState, useEffect } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTasks } from '@/context/TaskContext';
import { useTimerContext } from '@/context/TimerContext';
import { useBackgroundBrightness } from '@/hooks/useBackgroundBrightness';
import { useUnsplashBackground } from '@/hooks/useUnsplashBackground';
import { useActiveTimeEntry } from '@/hooks/timer/useActiveTimeEntry';
import SettingsModal from './SettingsModal';
import TaskListModal from './TaskListModal';
import NavigationBar from './NavigationBar';
import TimerClock from './timer/TimerClock';
import TimerHeader from './timer/TimerHeader';
import FocusAreaCard from './FocusAreaCard';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';

const PomodoroTimer: React.FC = () => {
  console.log("PomodoroTimer component rendering - start");
  
  try {
    const isMobile = useIsMobile();
    console.log("PomodoroTimer - isMobile:", isMobile);
    
    const { resetTasks } = useTasks();
    console.log("PomodoroTimer - useTasks hook loaded");
    
    const { session } = useAuth();
    console.log("PomodoroTimer - useAuth hook loaded, session:", !!session);
    
    const { timer } = useTimerContext();
    console.log("PomodoroTimer - useTimerContext hook loaded");
    
    const { finalizeCurrentEntry } = useActiveTimeEntry();
    console.log("PomodoroTimer - useActiveTimeEntry hook loaded");
    
    // Get background from Unsplash
    const { backgroundInfo } = useUnsplashBackground();
    console.log("PomodoroTimer - useUnsplashBackground hook loaded");
    
    // Detect background brightness
    const { isLight } = useBackgroundBrightness(backgroundInfo);
    console.log("PomodoroTimer - useBackgroundBrightness hook loaded");
    
    // UI state
    const [showSettings, setShowSettings] = useState(false);
    const [currentTime, setCurrentTime] = useState(new Date());

    // Update current time every second
    useEffect(() => {
      console.log("PomodoroTimer - Setting up time interval");
      const interval = setInterval(() => {
        setCurrentTime(new Date());
      }, 1000);
      
      return () => {
        console.log("PomodoroTimer - Cleaning up time interval");
        clearInterval(interval);
      };
    }, []);

    // Calculate appropriate timer size based on screen size - reverted to original
    const getTimerSize = () => {
      try {
        if (isMobile) {
          return Math.min(window.innerWidth - 40, 350);
        }
        return 525;
      } catch (error) {
        console.error("Error calculating timer size:", error);
        return 350; // fallback size
      }
    };
    
    const timerSize = getTimerSize();
    console.log("PomodoroTimer - Timer size calculated:", timerSize);

    const handleToggleTimer = () => {
      console.log("Toggle timer called, current state:", timer.isRunning);
      try {
        timer.toggleTimer();
      } catch (error) {
        console.error("Error toggling timer:", error);
      }
    };

    const handlePrevious = () => {
      console.log("Previous called");
      try {
        timer.goToPrevious();
      } catch (error) {
        console.error("Error going to previous:", error);
      }
    };

    const handleNext = () => {
      console.log("Next called");
      try {
        timer.goToNext();
      } catch (error) {
        console.error("Error going to next:", error);
      }
    };

    // Reset tasks when user logs out
    useEffect(() => {
      try {
        if (!session) {
          resetTasks();
          // Also finalize any active sessions when logging out
          if (finalizeCurrentEntry) {
            finalizeCurrentEntry('interrupted').catch(error => {
              console.error("Error finalizing session on logout:", error);
            });
          }
        }
      } catch (error) {
        console.error("Error in session effect:", error);
      }
    }, [session, resetTasks, finalizeCurrentEntry]);

    console.log("PomodoroTimer rendering - before return");

    const navigate = useNavigate();

    return (
      <div className="min-h-[100vh] flex flex-col">
        {/* Navigation Bar with background brightness info */}
        <NavigationBar 
          onOpenSettings={() => setShowSettings(true)}
          onOpenTaskList={() => navigate('/tasks')}
          isLightBackground={isLight}
        />

        {/* Time display in the top right */}
        <TimerHeader currentTime={currentTime} />

        {/* Main timer content */}
        <div className="flex-1 flex flex-col items-center justify-center px-2 pt-4 sm:pt-8 pb-16">
          <TimerClock 
            progress={timer.progress}
            minutes={timer.minutes}
            seconds={timer.seconds}
            isRunning={timer.isRunning}
            timerTitle={timer.timerTitle}
            isMobile={isMobile}
            timerSize={timerSize}
            onTogglePlay={handleToggleTimer}
            onPrevious={handlePrevious}
            onNext={handleNext}
            timerType={timer.timerType}
            onChangeTimerType={timer.changeTimerType}
          />

          {/* Unified Focus Area Card */}
          <div className="mt-6">
            <FocusAreaCard 
              sessions={timer.settings.sessions}
              currentSession={timer.currentSessionIndex}
              currentType={timer.timerType}
              isMobile={isMobile}
              activeIndex={timer.sequencePosition}
              onOpenTaskList={() => navigate('/tasks')}
              currentTime={currentTime}
              timerSize={timerSize}
            />
          </div>
        </div>

        {/* Settings Modal */}
        <SettingsModal 
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
          settings={timer.settings}
          onSave={timer.saveSettings}
        />
      </div>
    );
  } catch (error) {
    console.error("PomodoroTimer component error:", error);
    throw error; // Re-throw to trigger error boundary
  }
};

export default PomodoroTimer;
